import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch, type Ref } from 'vue';
import type { IEquipmentPage, IEquipmentQuery } from '@/types/equipment.types';

export const EQUIPMENT_PAGE_SIZES = [10, 20, 50, 100] as const;

type Filters = Omit<IEquipmentQuery, 'page' | 'pageSize'>;

// Une liste paginée côté serveur : changer un filtre ramène à la page 1, jamais une page qui n'existe plus.
export const useEquipmentList = <T>(
  key: string,
  filters: () => Filters,
  fetcher: (query: IEquipmentQuery) => Promise<IEquipmentPage<T>>,
  enabled: Ref<boolean> = ref(true),
) => {
  const page = ref(1);
  const pageSize = ref<number>(20);

  const query = computed<IEquipmentQuery>(() => ({
    ...filters(),
    page: page.value,
    pageSize: pageSize.value,
  }));

  watch([() => JSON.stringify(filters()), pageSize], () => {
    page.value = 1;
  });

  const list = useQuery({
    queryKey: computed(() => ['equipment', key, query.value]),
    queryFn: () => fetcher(query.value),
    enabled,
    placeholderData: (previous) => previous,
  });

  const rows = computed(() => list.data.value?.data ?? []);
  const total = computed(() => list.data.value?.total ?? 0);
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(total.value / pageSize.value)),
  );

  watch([pageCount, list.isFetching], () => {
    if (!list.isFetching.value && page.value > pageCount.value)
      page.value = pageCount.value;
  });

  return {
    rows,
    total,
    page,
    pageSize,
    pageCount,
    isLoading: list.isPending,
    isFetching: list.isFetching,
    isError: list.isError,
    refetch: list.refetch,
  };
};
